"use client";

import { useMemo, useState } from "react";
import {
  JD_APPS,
  JD_APP_MESH,
  publicAppUrl,
  type JdAppMeshId,
} from "@/lib/content";

/** Layout in a 1000×720 viewBox — Account sits as the hub. */
const NODE_POS: Record<JdAppMeshId, { x: number; y: number }> = {
  account: { x: 500, y: 360 },
  mail: { x: 220, y: 200 },
  drive: { x: 160, y: 400 },
  calendar: { x: 400, y: 120 },
  task: { x: 620, y: 120 },
  crm: { x: 780, y: 240 },
  cms: { x: 780, y: 480 },
  analytics: { x: 600, y: 580 },
  erp: { x: 320, y: 560 },
};

const APP_BY_ID = Object.fromEntries(JD_APPS.map((a) => [a.id, a])) as Record<
  JdAppMeshId,
  (typeof JD_APPS)[number]
>;

export function AppsWeb() {
  const [active, setActive] = useState<JdAppMeshId | null>("account");

  const edges = useMemo(() => {
    return JD_APP_MESH.map((e, i) => {
      const a = NODE_POS[e.from];
      const b = NODE_POS[e.to];
      const lit =
        active != null && (e.from === active || e.to === active);
      return { ...e, i, a, b, lit };
    });
  }, [active]);

  const activeEdges = useMemo(
    () =>
      active
        ? JD_APP_MESH.filter((e) => e.from === active || e.to === active)
        : [],
    [active],
  );

  return (
    <div className="apps-web">
      <div className="apps-web-stage" role="img" aria-label="JD Apps integration web">
        <svg
          className="apps-web-svg"
          viewBox="0 0 1000 720"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <radialGradient id="apps-web-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--gold)" stopOpacity="0.22" />
              <stop offset="70%" stopColor="var(--gold)" stopOpacity="0.04" />
              <stop offset="100%" stopColor="var(--gold)" stopOpacity="0" />
            </radialGradient>
            <filter id="apps-web-soft" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.2" />
            </filter>
          </defs>

          <circle cx="500" cy="360" r="210" fill="url(#apps-web-glow)" />

          {edges.map((e) => (
            <g key={`${e.from}-${e.to}`}>
              <line
                className={"apps-web-link" + (e.lit ? " is-lit" : "")}
                x1={e.a.x}
                y1={e.a.y}
                x2={e.b.x}
                y2={e.b.y}
              />
              {e.lit && (
                <circle className="apps-web-packet" r="3.5">
                  <animateMotion
                    dur={`${2.4 + (e.i % 4) * 0.35}s`}
                    repeatCount="indefinite"
                    path={`M${e.a.x},${e.a.y} L${e.b.x},${e.b.y}`}
                  />
                </circle>
              )}
            </g>
          ))}
        </svg>

        {(Object.keys(NODE_POS) as JdAppMeshId[]).map((id) => {
          const app = APP_BY_ID[id];
          const pos = NODE_POS[id];
          const on = active === id;
          const linked =
            active != null &&
            (id === active ||
              JD_APP_MESH.some(
                (e) =>
                  (e.from === active && e.to === id) ||
                  (e.to === active && e.from === id),
              ));
          return (
            <a
              key={id}
              href={publicAppUrl(app.env)}
              target="_blank"
              rel="noreferrer"
              className={
                "apps-web-node" +
                (on ? " is-active" : "") +
                (linked ? " is-linked" : "") +
                (id === "account" ? " is-hub" : "")
              }
              style={{ left: `${(pos.x / 1000) * 100}%`, top: `${(pos.y / 720) * 100}%` }}
              onMouseEnter={() => setActive(id)}
              onFocus={() => setActive(id)}
              title={app.name}
              aria-label={app.name}
            >
              <img
                src={app.icon}
                alt=""
                width={48}
                height={48}
                loading="lazy"
                decoding="async"
              />
              <span>{app.name.replace(/^JD /, "")}</span>
            </a>
          );
        })}
      </div>

      <div className="apps-web-caption" aria-live="polite">
        <div className="mono-label">
          {active ? `${APP_BY_ID[active].name} links` : "Suite mesh"}
        </div>
        <ul>
          {(activeEdges.length ? activeEdges : JD_APP_MESH.slice(0, 6)).map((e) => {
            const other =
              active == null
                ? `${APP_BY_ID[e.from].name} ↔ ${APP_BY_ID[e.to].name}`
                : APP_BY_ID[e.from === active ? e.to : e.from].name;
            return (
              <li key={`${e.from}-${e.to}`}>
                <strong>{other.replace(/^JD /, "")}</strong>
                <span>{e.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
